// src/modules/orion-shell/AgentDock.tsx
//
// Agente interno siempre a la mano: orbe flotante (símbolo de Orion vivo) que abre
// el chat del agente como panel flotante SIN salir de la pantalla actual.
// Navegación por intención (misma técnica que el copiloto de los sitios públicos):
// «llévame a leads», «abre WhatsApp», «muéstrame el calendario» → la página de
// atrás cambia mientras el chat sigue abierto. Determinista: no depende de que el
// modelo emita un marcador; solo navega a módulos que el usuario YA puede abrir.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { ChatPane } from '@/components/assistant/ChatPane';
import { useSharedDock, type DockReturnState } from '@/components/assistant/activeThread';
import { OrionOrb } from './OrionOrb';
import { useDashboardShell } from '@/context/DashboardShellContext';
import { shellBrand } from './brand';

/** Avatar del agente: `dashboard.brand.agent_avatar` de la org (opt-in) o el orbe de Orion. */
function AgentAvatar({ src, size, energy }: { src: string | null; size: number; energy: number }) {
  if (src) {
    return <img src={src} alt="" width={size} height={size} className="rounded-full object-cover" style={{ width: size, height: size }} draggable={false} />;
  }
  return <OrionOrb size={size} energy={energy} />;
}

export interface DockTarget {
  /** etiqueta visible (p. ej. «Leads») */
  label: string;
  /** ruta del dashboard */
  path: string;
  /** palabras con las que la gente lo pide */
  words: string[];
}

// Sinónimos por entrada del menú (sin acentos, minúsculas).
export const NAV_WORDS: Record<string, string[]> = {
  inicio: ['inicio', 'home', 'tablero', 'resumen'],
  agentes: ['agentes', 'agente', 'asistentes'],
  sitio: ['sitio', 'pagina web', 'web', 'editor del sitio', 'mi sitio'],
  cambios: ['cambios del sitio', 'cambios', 'aprobaciones del sitio'],
  whatsapp: ['whatsapp', 'wasap', 'chats de clientes'],
  leads: ['leads', 'prospectos', 'clientes', 'crm'],
  calendario: ['calendario', 'citas', 'agenda'],
  contenido: ['contenido', 'publicaciones', 'redes', 'posts'],
  reportes: ['reportes', 'metricas', 'estadisticas', 'analitica'],
  diagnosticos: ['diagnosticos', 'diagnostico', 'mis empresas', 'empresas cliente'],
  tickets: ['tickets', 'ticket', 'solicitudes', 'incidencias', 'fallas reportadas'],
  config: ['configuracion', 'ajustes', 'empresa'],
};

const GO_INTENT = /\b(ll[eé]vame|ir a|vamos a|abre|abrir|ensename|enseñame|muestrame|muéstrame|quiero ver|ver (?:mis|los|las|el|la)|pasa a|cambia a|navega)\b/i;

const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export function matchTarget(text: string, targets: DockTarget[]): DockTarget | null {
  const t = ` ${norm(text)} `;
  let best: { tg: DockTarget; len: number } | null = null;
  for (const tg of targets) {
    for (const w of [tg.label, ...tg.words]) {
      const nw = norm(w);
      if (nw.length >= 3 && new RegExp(`[^a-z0-9]${nw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[^a-z0-9]`).test(t)) {
        if (!best || nw.length > best.len) best = { tg, len: nw.length };
      }
    }
  }
  return best?.tg ?? null;
}

export function AgentDock({ targets, hidden }: { targets: DockTarget[]; hidden?: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  // 🔁 Abierto/cerrado + hilo compartido con la pantalla completa (activeThread.ts).
  const { open, setOpen, thread: dockThread } = useSharedDock();
  // `shown` mantiene el panel en el DOM durante la animación de cierre.
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (open) { setShown(true); return; }
    const t = window.setTimeout(() => setShown(false), 420);
    return () => window.clearTimeout(t);
  }, [open]);
  const [hover, setHover] = useState(false);
  const [chip, setChip] = useState<DockTarget | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const go = useCallback((tg: DockTarget) => { navigate(tg.path); setChip(null); }, [navigate]);

  // Llamado por ChatPane cuando el usuario envía un mensaje.
  const onUserSend = useCallback((text: string) => {
    const tg = matchTarget(text, targets);
    if (!tg) { setChip(null); return; }
    if (GO_INTENT.test(text)) go(tg); // intención clara de ir → navega ya
    else setChip(tg); // solo lo menciona → ofrece «Ver …»
  }, [targets, go]);

  useEffect(() => {
    if (!open) return;
    // defaultPrevented: Esc ya lo usó el menú de @ / # o el renombrado del título.
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !e.defaultPrevented) { setOpen(false); btnRef.current?.focus(); } };
    window.addEventListener('keydown', onKey);
    panelRef.current?.querySelector<HTMLElement>('textarea,input')?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // ⌘/Ctrl + J abre o cierra el agente desde cualquier pantalla.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'j') { e.preventDefault(); setOpen((o) => !o); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const headers = useMemo(() => {
    const org = localStorage.getItem('org_id');
    return org ? { 'x-org-id': org } : {};
  }, []);
  const minimize = useCallback(() => { setOpen(false); btnRef.current?.focus(); }, []);
  // «Ampliar»: la misma conversación en el Asistente a pantalla completa; al
  // «Minimizar» allá se regresa a esta pantalla con el flotante abierto.
  const expand = useCallback((id: string | null) => {
    setOpen(false);
    const state: DockReturnState = { fromDock: location.pathname + location.search };
    navigate(id ? `/m/orion/asistente?hilo=${encodeURIComponent(id)}` : '/m/orion/asistente', { state });
  }, [navigate, location.pathname, location.search]);

  const brand = shellBrand(useDashboardShell().config);
  const avatar = brand.agentAvatar;

  const pane = useMemo(() => (
    <ChatPane
      key={dockThread ?? 'base'}
      variant="dock"
      threadId={dockThread}
      webChatHeaders={headers}
      shareThread
      hideThinking
      mentions
      modelPicker
      onUserSend={onUserSend}
      dockTitle={avatar ? brand.assistantName : 'Agente'}
      dockLeading={<AgentAvatar src={avatar} size={26} energy={0.4} />}
      assistantAvatarUrl={avatar ?? undefined}
      dockBelowHeader={chip && (
        <button type="button" onClick={() => go(chip)}
                className="mx-3 mt-2 inline-flex items-center gap-1.5 self-start rounded-full border border-border bg-muted px-3 py-1 text-xs font-medium text-foreground hover:bg-muted/70">
          Ver {chip.label} <ArrowRight size={12} />
        </button>
      )}
      onExpand={expand}
      onMinimize={minimize}
    />
  ), [onUserSend, dockThread, headers, chip, go, expand, minimize, avatar, brand.assistantName]);

  if (hidden) return null;

  return (
    <>
      {shown && (
        <div
          ref={panelRef}
          data-open={open ? '' : undefined}
          role="dialog"
          aria-label={avatar ? `Agente de ${brand.assistantName}` : 'Agente de Orion'}
          className="orion-dock orion-dock-anim fixed z-[110] flex flex-col overflow-hidden border border-border bg-background/95 shadow-2xl backdrop-blur-xl
                     inset-x-2 bottom-2 top-[12svh] rounded-3xl
                     sm:inset-x-auto sm:right-5 sm:bottom-24 sm:top-auto sm:h-[min(640px,calc(100svh-8rem))] sm:w-[420px]"
        >
          <div className="min-h-0 flex-1">{pane}</div>
        </div>
      )}

      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onFocus={() => setHover(true)}
        onBlur={() => setHover(false)}
        aria-expanded={open}
        aria-label={open ? 'Cerrar agente (Ctrl+J)' : 'Hablar con el agente (Ctrl+J)'}
        title="Agente · Ctrl+J"
        data-open={open ? '' : undefined}
        className="orion-orb-btn orion-orb-anim fixed bottom-5 right-5 z-[111] grid h-14 w-14 place-items-center rounded-full border border-border bg-background/80 shadow-xl backdrop-blur-xl
                   transition-transform duration-300 hover:scale-105 active:scale-95 pb-[env(safe-area-inset-bottom)]"
      >
        <AgentAvatar src={avatar} size={40} energy={open ? 1 : hover ? 1 : 0} />
      </button>
    </>
  );
}
